// Waiver authors add a checkbox by typing "[ ]" (unchecked by default) or
// "[x]" (checked by default) in the waiver description. A contract stores the
// final state of each one as a JSON array of booleans, by position.
const WAIVER_CHECKBOX_PATTERN = /\[( |x|X)\]/g;

export const WAIVER_CHECKBOX_TOKEN = "[ ]";

export const getWaiverCheckDefaults = (text?: string | null): boolean[] =>
  ((text ?? "").match(WAIVER_CHECKBOX_PATTERN) ?? []).map(
    (token) => token[1].toLowerCase() === "x"
  );

// Aligns saved state to the waiver's current checkboxes, falling back to the
// author's default for any checkbox the saved state doesn't cover.
export const parseWaiverChecks = (
  raw: string | null | undefined,
  text?: string | null
): boolean[] => {
  const defaults = getWaiverCheckDefaults(text);
  let saved: unknown = [];

  try {
    saved = raw ? JSON.parse(raw) : [];
  } catch {
    saved = [];
  }

  const savedChecks = Array.isArray(saved) ? saved : [];

  return defaults.map((value, index) =>
    typeof savedChecks[index] === "boolean" ? savedChecks[index] : value
  );
};

// Read-only boxes are drawn with a border rather than the ☑/☐ glyphs, which
// fall back to differently sized emoji fonts and misalign when printed.
// Inline styles so the print iframe never depends on the app's stylesheet.
const READ_ONLY_BOX_STYLE = [
  "display:inline-block",
  "box-sizing:border-box",
  "width:1em",
  "height:1em",
  "border:1px solid #000",
  "line-height:0.9em",
  "text-align:center",
  "font-family:Arial, sans-serif",
  "font-size:1em",
  "vertical-align:-0.15em",
  "margin-right:0.4em",
].join(";");

// Interactive mode renders real inputs tagged with data-waiver-check so the
// container can pick up clicks; read-only mode renders drawn boxes, which
// print reliably unlike disabled form controls.
export const renderWaiverCheckboxes = (
  html: string,
  checks: boolean[],
  interactive: boolean
): string => {
  let index = 0;

  return html.replace(WAIVER_CHECKBOX_PATTERN, (_, mark: string) => {
    const current = index++;
    const checked = checks[current] ?? mark.toLowerCase() === "x";

    if (interactive) {
      return `<input type="checkbox" class="waiver-check" data-waiver-check="${current}"${
        checked ? " checked" : ""
      } />`;
    }

    // &#10003;&#xFE0E; is a check mark forced to text (not emoji) rendering.
    return `<span class="waiver-check" data-checked="${checked}" style="${READ_ONLY_BOX_STYLE}">${
      checked ? "&#10003;&#xFE0E;" : ""
    }</span>`;
  });
};
